// ✅ src/components/screens/SellerDashboardScreen.js (FULL - UPDATED + NOTIF BADGE COUNT)
// ✅ Subscription gate added for Share link + Preview (like ProductsScreen)
// ✅ Shows “3 days left” banner ONLY when dueSoon (<= 3 days)
// ✅ Preview opens inside app to /s/:storeSlug (PublicStoreScreen route)
// ✅ Added Sidebar item: ⭐ Subscription -> /seller/subscription
// ✅ NEW: 🔔 Notifications badge count (unread)
// ✅ NEW: 📱 QR Code (gated like Share link + Preview)

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code"; // ✅ NEW
import { auth, db } from "../../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

/* ---------------- helpers ---------------- */
function safe(v) {
  return String(v ?? "").trim();
}
function initials(name) {
  const t = safe(name);
  if (!t) return "S";
  const parts = t.split(" ").filter(Boolean);
  const a = parts[0]?.[0] || "S";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase();
}
function moneyGHS(n) {
  const num = Number(n || 0);
  if (Number.isNaN(num)) return "GHS 0";
  return `GHS ${num.toLocaleString()}`;
}
function toMs(ts) {
  try {
    if (!ts) return 0;
    if (ts?.toDate) return ts.toDate().getTime();
    if (typeof ts === "number") return ts;
    if (ts?.seconds != null) return ts.seconds * 1000;
    return 0;
  } catch {
    return 0;
  }
}
function fmtPhone(v) {
  const t = safe(v);
  return t || "";
}
function capStatus(s) {
  const t = safe(s);
  if (!t) return "—";
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}
function normalizePayLabel(type) {
  const t = safe(type).toUpperCase();
  if (t === "MOMO" || t === "MOBILE_MONEY") return "MoMo";
  if (t === "BANK" || t === "BANK_TRANSFER") return "Bank";
  if (t === "CASH") return "Cash";
  return t || "—";
}
function normalizeDelLabel(type) {
  const t = safe(type).toUpperCase();
  if (t.includes("PICK")) return "Local Pickup";
  if (t.includes("MEET")) return "Meet-up Location";
  if (t.includes("DELIV")) return "Delivery";
  return safe(type) || "Delivery";
}
function daysLeftFromMs(ms) {
  if (!ms) return null;
  const diff = ms - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}
function fmtDate(ms) {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

/* ✅ Ensure users/{uid} exists (create/update) */
async function ensureUserDoc(u) {
  if (!u?.uid) return;

  const ref = doc(db, "users", u.uid);

  try {
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      await setDoc(ref, {
        uid: u.uid,
        displayName: safe(u.displayName) || "User",
        email: safe(u.email),
        photoURL: safe(u.photoURL),

        role: "SELLER",
        isBanned: false,
        banReason: "",

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return;
    }

    await setDoc(
      ref,
      {
        displayName: safe(u.displayName),
        email: safe(u.email),
        photoURL: safe(u.photoURL),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.log("ensureUserDoc failed:", e);
  }
}

/* ---------------- status + tracking mapping ---------------- */
// ✅ FIX: removed unused TRACK_STEPS to satisfy ESLint (no-unused-vars)

function statusToStep(status) {
  const s = safe(status).toUpperCase();
  if (s === "NEW") return "PLACED";
  if (s === "PENDING_CONFIRMATION") return "PLACED";
  if (s === "CONFIRMED") return "CONFIRMED";
  if (s === "PACKED") return "PACKED";
  if (s === "OUT_FOR_DELIVERY") return "OUT_FOR_DELIVERY";
  if (s === "DONE" || s === "DELIVERED") return "DELIVERED";
  return "PLACED";
}

function stepToStatus(step) {
  const s = safe(step).toUpperCase();
  if (s === "PLACED") return "NEW";
  if (s === "CONFIRMED") return "CONFIRMED";
  if (s === "PACKED") return "PACKED";
  if (s === "OUT_FOR_DELIVERY") return "OUT_FOR_DELIVERY";
  if (s === "DELIVERED") return "DONE";
  return "NEW";
}

function stepTitle(step) {
  switch (safe(step).toUpperCase()) {
    case "PLACED":
      return "Order placed";
    case "CONFIRMED":
      return "Confirmed";
    case "PACKED":
      return "Packed";
    case "OUT_FOR_DELIVERY":
      return "Out for delivery";
    case "DELIVERED":
      return "Delivered";
    default:
      return safe(step) || "—";
  }
}

function currentStepFromOrder(o) {
  return safe(o?.tracking?.currentStep) || statusToStep(o?.status);
}

function makePublicStoreUrl(slugOrUid) {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  return `${base}/s/${encodeURIComponent(slugOrUid)}`;
}
async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/* mini chart for Reports card */
function buildWeeklySeries(orders) {
  const now = new Date();
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    d.setHours(0, 0, 0, 0);
    days.push(d.getTime());
  }

  const sums = new Array(7).fill(0);

  for (const o of orders || []) {
    const ms = toMs(o?.createdAt);
    if (!ms) continue;

    const d = new Date(ms);
    d.setHours(0, 0, 0, 0);
    const dayStart = d.getTime();

    const idx = days.indexOf(dayStart);
    if (idx >= 0) sums[idx] += Number(o?.total || 0);
  }

  return sums;
}
function svgPathFromSeries(series, w = 260, h = 110, pad = 10) {
  const max = Math.max(1, ...series);
  const min = Math.min(0, ...series);
  const range = Math.max(1, max - min);

  const stepX = (w - pad * 2) / Math.max(1, series.length - 1);

  const pts = series.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + (h - pad * 2) * (1 - (v - min) / range);
    return { x, y };
  });

  if (!pts.length) return "";
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) d += ` L ${pts[i].x} ${pts[i].y}`;
  return d;
}

/* ---------------- component ---------------- */
export default function SellerDashboardScreen({
  goBack,
  goManageProducts,
  goAddProduct,
  goOrders,
  goSettings,
  goPaymentsAndDelivery,
  goStoreProfile,
  goNotifications,
  goReports,
  goContactUs,
}) {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [deliveries, setDeliveries] = useState([]);

  const [productCount, setProductCount] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [salesRecent, setSalesRecent] = useState(0);
  const [newOrdersCount, setNewOrdersCount] = useState(0);

  const [ordersTab, setOrdersTab] = useState("recent"); // recent | all | completed

  // ✅ NEW: unread notifications count (badge)
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);

  // ✅ NEW: QR modal state (gated like share link)
  const [qrOpen, setQrOpen] = useState(false);
  const openQr = () => setQrOpen(true);
  const closeQr = () => setQrOpen(false);

  // ✅ mobile sidebar
  const [sideOpen, setSideOpen] = useState(false);
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 980) setSideOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // 1) auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);

      if (!u) {
        setMsg("Please login first.");
        setLoading(false);
        setStore(null);
        setProducts([]);
        setOrders([]);
        setPayments([]);
        setDeliveries([]);
        setUnreadNotifCount(0);
        return;
      }

      setMsg("");
      await ensureUserDoc(u);
    });

    return () => unsub();
  }, []);

  // 2) realtime fetches
  useEffect(() => {
    if (!user) return;

    let alive = true;
    const unsubs = [];
    const storeId = user.uid;

    async function boot() {
      setLoading(true);
      setMsg("");

      try {
        // store
        const storeRef = doc(db, "stores", storeId);
        const unsubStore = onSnapshot(
          storeRef,
          (snap) => {
            if (!alive) return;
            if (!snap.exists()) {
              setStore(null);
              setMsg("No store found for this account.");
              return;
            }
            setStore({ id: storeId, ...(snap.data() || {}) });
          },
          (err) => alive && setMsg(err?.message || "Failed to load store.")
        );
        unsubs.push(unsubStore);

        // payment methods
        const payRef = collection(db, "stores", storeId, "paymentMethods");
        const unsubPay = onSnapshot(
          payRef,
          (qs) => alive && setPayments(qs.docs.map((d) => ({ id: d.id, ...d.data() }))),
          (err) => alive && setMsg(err?.message || "Payment methods blocked (rules?)")
        );
        unsubs.push(unsubPay);

        // delivery options
        const delRef = collection(db, "stores", storeId, "deliveryOptions");
        const unsubDel = onSnapshot(
          delRef,
          (qs) => alive && setDeliveries(qs.docs.map((d) => ({ id: d.id, ...d.data() }))),
          (err) => alive && setMsg(err?.message || "Delivery options blocked (rules?)")
        );
        unsubs.push(unsubDel);

        // products (recent list)
        const productsQ = query(
          collection(db, "products"),
          where("storeId", "==", storeId),
          orderBy("updatedAt", "desc"),
          limit(10)
        );
        const unsubProducts = onSnapshot(
          productsQ,
          (qs) => {
            if (!alive) return;
            const rows = qs.docs.map((d) => ({ id: d.id, ...d.data() }));
            setProducts(rows);

            const low = rows.filter((p) => {
              const s = Number(p.stock || 0);
              return s > 0 && s <= 2;
            }).length;
            setLowStockCount(low);
          },
          (err) => alive && setMsg(err?.message || "Failed to load products (index/rules?).")
        );
        unsubs.push(unsubProducts);

        // products count (one-time)
        try {
          const allProductsQ = query(
            collection(db, "products"),
            where("storeId", "==", storeId),
            limit(2500)
          );
          const allSnap = await getDocs(allProductsQ);
          if (alive) setProductCount(allSnap.size);
        } catch {}

        // orders (recent list)
        const ordersQ = query(
          collection(db, "orders"),
          where("storeId", "==", storeId),
          orderBy("createdAt", "desc"),
          limit(12)
        );
        const unsubOrders = onSnapshot(
          ordersQ,
          (qs) => {
            if (!alive) return;
            const rows = qs.docs.map((d) => ({ id: d.id, ...d.data() }));
            setOrders(rows);

            const newOnes = rows.filter((o) => safe(o.status).toUpperCase() === "NEW").length;
            setNewOrdersCount(newOnes);

            const sum = rows.reduce((acc, o) => acc + Number(o.total || 0), 0);
            setSalesRecent(sum);
          },
          (err) => alive && setMsg(err?.message || "Failed to load orders (index/rules?).")
        );
        unsubs.push(unsubOrders);

        // ✅ NEW: notifications (unread badge count)
        const notifQ = query(
          collection(db, "notifications", storeId, "items"),
          where("read", "==", false),
          limit(50)
        );
        const unsubNotif = onSnapshot(
          notifQ,
          (qs) => {
            if (!alive) return;
            setUnreadNotifCount(qs.size || 0);
          },
          () => {
            if (!alive) return;
            setUnreadNotifCount(0);
          }
        );
        unsubs.push(unsubNotif);
      } catch (e) {
        if (alive) setMsg(e?.message || "Dashboard failed to load.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    boot();

    return () => {
      alive = false;
      unsubs.forEach((fn) => {
        try {
          fn();
        } catch {}
      });
    };
  }, [user]);

  const computed = useMemo(() => {
    const storeName = safe(store?.storeName) || safe(user?.displayName) || "Your Store";
    const storeSlug = safe(store?.storeSlug);
    const logoUrl = safe(store?.logoUrl) || safe(user?.photoURL);
    const bannerUrl = safe(store?.bannerUrl);
    return { storeName, storeSlug, logoUrl, bannerUrl };
  }, [store, user]);

  // ✅ subscription logic (ACTIVE gate + dueSoon banner)
  const activeUntilMs = useMemo(() => {
    return toMs(store?.activeUntil || store?.subscriptionEndsAt || store?.expiresAt);
  }, [store]);

  const daysLeft = useMemo(() => daysLeftFromMs(activeUntilMs), [activeUntilMs]);

  const isStoreActive = useMemo(() => {
    const sub = safe(store?.subscriptionStatus).toUpperCase();
    if (store?.isActive === true && sub === "ACTIVE") return true;
    if (activeUntilMs) return activeUntilMs > Date.now();
    return false;
  }, [store, activeUntilMs]);

  const dueSoon = useMemo(() => {
    if (daysLeft == null) return false;
    return daysLeft <= 3 && daysLeft > 0;
  }, [daysLeft]);

  const expired = useMemo(() => {
    if (daysLeft == null) return false;
    return daysLeft <= 0 && activeUntilMs > 0;
  }, [daysLeft, activeUntilMs]);

  const dueLabel = useMemo(() => {
    if (daysLeft == null) return "";
    if (daysLeft <= 0) return "Expired — Renew now";
    if (daysLeft === 1) return "1 day left — Renew";
    return `${daysLeft} days left — Renew`;
  }, [daysLeft]);

  const publicKey = computed.storeSlug || user?.uid;
  const publicUrl = useMemo(() => makePublicStoreUrl(publicKey || ""), [publicKey]);

  const enabledPayments = useMemo(
    () => (payments || []).filter((p) => p?.isEnabled !== false),
    [payments]
  );
  const enabledDeliveries = useMemo(
    () => (deliveries || []).filter((d) => d?.isEnabled !== false),
    [deliveries]
  );

  const ordersForTable = useMemo(() => {
    const rows = orders || [];
    if (ordersTab === "completed") {
      return rows.filter((o) => safe(o.status).toUpperCase() === "DONE");
    }
    return rows;
  }, [orders, ordersTab]);

  const reportSeries = useMemo(() => buildWeeklySeries(orders), [orders]);
  const reportPath = useMemo(() => svgPathFromSeries(reportSeries), [reportSeries]);

  const closeSidebarMobile = () => setSideOpen(false);

  const pushTracking = async (order, step, text) => {
    if (!user) return;
    const orderId = safe(order?.id || order);
    if (!orderId) return;

    const nextStatus = stepToStatus(step);

    try {
      setMsg("");

      await updateDoc(doc(db, "orders", orderId), {
        status: nextStatus,
        updatedAt: serverTimestamp(),
        "tracking.currentStep": safe(step).toUpperCase(),
        "tracking.history": arrayUnion({
          step: safe(step).toUpperCase(),
          text: safe(text) || stepTitle(step),
          at: serverTimestamp(),
        }),
      });

      setMsg(`✅ Tracking updated: ${stepTitle(step)}`);
      setTimeout(() => setMsg(""), 1500);
    } catch (e) {
      setMsg(e?.message || "Failed to update tracking.");
    }
  };

  const confirmPayment = async (order) => {
    if (!user) return;

    const orderId = safe(order?.id || order);
    if (!orderId) return;

    try {
      setMsg("");

      await updateDoc(doc(db, "orders", orderId), {
        confirmedAt: serverTimestamp(),
        confirmedBy: user.uid,
        sellerConfirmNote: "Payment received",

        "payment.sellerConfirmed": true,
        "payment.confirmedAt": serverTimestamp(),
        "payment.confirmedBy": user.uid,

        status: "CONFIRMED",
        updatedAt: serverTimestamp(),

        "tracking.currentStep": "CONFIRMED",
        "tracking.history": arrayUnion({
          step: "CONFIRMED",
          text: "Seller confirmed payment",
          at: serverTimestamp(),
        }),
      });

      setMsg("✅ Payment confirmed + tracking updated.");
      setTimeout(() => setMsg(""), 1500);
    } catch (e) {
      setMsg(e?.message || "Failed to confirm payment.");
    }
  };

  return (
    <div className="sd-page">
      <style>{css}</style>

      <div className="sd-container">
        {loading ? <div className="sd-alert">Loading dashboard…</div> : null}
        {msg ? (
          <div className={`sd-alert ${msg.includes("✅") ? "" : "sd-alert--err"}`}>{msg}</div>
        ) : null}

        {!loading && store ? (
          <div className="sd-card sd-card--outer">
            {/* HERO */}
            <div className="sd-hero">
              {computed.bannerUrl ? (
                <img className="sd-hero-img" src={computed.bannerUrl} alt="banner" />
              ) : null}
              <div className="sd-hero-overlay" />
              <div className="sd-hero-sign">
                <div className="sd-hero-sign-inner">{computed.storeName}</div>
              </div>
            </div>

            {/* TOP NAV */}
            <div className="sd-topnav">
              <button className="sd-hamb" onClick={() => setSideOpen(true)} aria-label="Open menu">
                ☰
              </button>

              <div className="sd-topnav-links">
                <button className="sd-topnav-link" onClick={goBack}>
                  Home
                </button>

                <button className="sd-topnav-link is-active" onClick={goManageProducts}>
                  Products
                </button>

                <button
                  className="sd-topnav-link"
                  onClick={() => (goContactUs ? goContactUs() : goStoreProfile?.())}
                >
                  Contact Us
                </button>
              </div>

              <div className="sd-topnav-right">
                <div className="sd-topnav-actions">
                  {/* ✅ Due soon pill */}
                  {dueSoon || expired ? (
                    <button
                      className={`sd-topnav-pill ${expired ? "is-expired" : ""}`}
                      onClick={() => navigate("/seller/subscription")}
                      title="Manage subscription"
                    >
                      ⏰ {dueLabel}
                    </button>
                  ) : null}

                  {/* ✅ Share link gate (like ProductsScreen) */}
                  <button
                    className="sd-topnav-ghost"
                    onClick={async () => {
                      if (!isStoreActive) {
                        navigate("/seller/pay");
                        return;
                      }
                      const ok = await copyToClipboard(publicUrl);
                      setMsg(ok ? "✅ Store link copied!" : `Copy failed. Link: ${publicUrl}`);
                      setTimeout(() => setMsg(""), 1400);
                    }}
                    title={!isStoreActive ? "Pay to activate store link" : "Copy store link"}
                  >
                    🔗 Share link
                  </button>

                  {/* ✅ NEW: QR Code (gated like Share/Preview) */}
                  <button
                    className="sd-topnav-ghost"
                    onClick={() => {
                      if (!isStoreActive) {
                        navigate("/seller/pay");
                        return;
                      }
                      openQr();
                    }}
                    title={!isStoreActive ? "Pay to activate QR code" : "Open QR code"}
                  >
                    📱 QR Code
                  </button>

                  {/* ✅ Preview opens INSIDE app to PublicStoreScreen route */}
                  <button
                    className="sd-topnav-ghost"
                    onClick={() => {
                      if (!isStoreActive) {
                        navigate("/seller/pay");
                        return;
                      }
                      const slug = safe(store?.storeSlug);
                      if (slug) navigate(`/s/${slug}`);
                      else navigate(`/s/${encodeURIComponent(user?.uid || "")}`);
                    }}
                    title={!isStoreActive ? "Pay to activate preview" : "Preview your store"}
                  >
                    👁️ Preview
                  </button>

                  {/* ✅ Notifications with badge */}
                  {goNotifications ? (
                    <button className="sd-topnav-ghost" onClick={goNotifications}>
                      🔔 Notifications
                      {unreadNotifCount > 0 ? (
                        <span className="sd-notif-badge">
                          {unreadNotifCount > 99 ? "99+" : unreadNotifCount}
                        </span>
                      ) : null}
                    </button>
                  ) : null}
                </div>

                <button className="sd-topnav-profile" onClick={goStoreProfile || goSettings}>
                  <span className="sd-avatar sd-avatar--sm">
                    {computed.logoUrl ? (
                      <img src={computed.logoUrl} alt="avatar" />
                    ) : (
                      <span>{initials(computed.storeName)}</span>
                    )}
                  </span>
                  <span className="sd-topnav-name">{safe(user?.displayName) || "Seller"}</span>
                  <span className="sd-caret">▾</span>
                </button>
              </div>
            </div>

            {/* ✅ ONLY when due */}
            {dueSoon || expired ? (
              <div className={`sd-subwarn ${expired ? "is-expired" : ""}`}>
                <div className="sd-subwarn-left">
                  <div className="sd-subwarn-title">{dueLabel}</div>
                  <div className="sd-subwarn-sub">
                    Active until <b>{fmtDate(activeUntilMs)}</b>. Renew to keep link + preview active.
                  </div>
                </div>

                <button className="sd-subwarn-btn" onClick={() => navigate("/seller/subscription")}>
                  Manage
                </button>
              </div>
            ) : null}

            {/* BODY */}
            <div className="sd-body">
              <div className={`sd-overlay ${sideOpen ? "is-open" : ""}`} onClick={closeSidebarMobile} />

              {/* LEFT SIDEBAR */}
              <aside className={`sd-sidebar ${sideOpen ? "is-open" : ""}`}>
                <div className="sd-sidecard">
                  <div className="sd-sidehead">
                    <div className="sd-avatar sd-avatar--lg">
                      {computed.logoUrl ? (
                        <img src={computed.logoUrl} alt="logo" />
                      ) : (
                        <span>{initials(computed.storeName)}</span>
                      )}
                    </div>

                    <div>
                      <div className="sd-sidename">{computed.storeName}</div>
                      <div className="sd-sideactive">
                        <span className="sd-dot" /> Active seller
                      </div>
                    </div>

                    <button className="sd-close" onClick={closeSidebarMobile} aria-label="Close menu">
                      ✕
                    </button>
                  </div>

                  <div className="sd-sidenav">
                    <button className="sd-navbtn is-active" onClick={closeSidebarMobile}>
                      <span className="sd-ico">🏠</span> Dashboard
                    </button>

                    <button
                      className="sd-navbtn"
                      onClick={() => {
                        closeSidebarMobile();
                        goManageProducts?.();
                      }}
                    >
                      <span className="sd-ico">📦</span> Products
                    </button>

                    <button
                      className="sd-navbtn"
                      onClick={() => {
                        closeSidebarMobile();
                        goOrders?.();
                      }}
                    >
                      <span className="sd-ico">🧾</span> Orders
                    </button>

                    <button
                      className="sd-navbtn"
                      onClick={() => {
                        closeSidebarMobile();
                        (goPaymentsAndDelivery || goSettings)?.();
                      }}
                    >
                      <span className="sd-ico">💳</span> Payment &amp; Delivery
                    </button>

                    {/* ✅ NEW: Subscription */}
                    <button
                      className="sd-navbtn"
                      onClick={() => {
                        closeSidebarMobile();
                        navigate("/seller/subscription");
                      }}
                    >
                      <span className="sd-ico">⭐</span> Subscription
                      {dueSoon || expired ? <span className="sd-navtag">Due</span> : null}
                    </button>

                    <button
                      className="sd-navbtn"
                      onClick={() => {
                        closeSidebarMobile();
                        goContactUs?.();
                      }}
                    >
                      <span className="sd-ico">📞</span> Contact Us
                    </button>

                    <button
                      className="sd-navbtn"
                      onClick={() => {
                        closeSidebarMobile();
                        (goStoreProfile || goSettings)?.();
                      }}
                    >
                      <span className="sd-ico">⚙️</span> Store Settings
                    </button>

                    <button
                      className="sd-navbtn"
                      onClick={() => {
                        closeSidebarMobile();
                        goReports?.();
                      }}
                    >
                      <span className="sd-ico">📊</span> Reports
                    </button>

                    {goNotifications ? (
                      <button
                        className="sd-navbtn"
                        onClick={() => {
                          closeSidebarMobile();
                          goNotifications();
                        }}
                      >
                        <span className="sd-ico">🔔</span> Notifications
                        {unreadNotifCount > 0 ? <span className="sd-navtag">{unreadNotifCount}</span> : null}
                      </button>
                    ) : null}

                    <div className="sd-footnote">© {new Date().getFullYear()}</div>
                  </div>
                </div>

                <div className="sd-sidecard">
                  <div className="sd-sidecard-title">Quick Actions</div>

                  <button
                    className="sd-actionbtn"
                    onClick={() => {
                      closeSidebarMobile();
                      (goAddProduct || goManageProducts)?.();
                    }}
                  >
                    <span className="sd-actionico">➕</span> Add New Product
                  </button>

                  <button
                    className="sd-actionbtn"
                    onClick={() => {
                      closeSidebarMobile();
                      goManageProducts?.();
                    }}
                  >
                    <span className="sd-actionico">🧰</span> Manage Products
                  </button>

                  <button
                    className="sd-actionbtn"
                    onClick={() => {
                      closeSidebarMobile();
                      goOrders?.();
                    }}
                  >
                    <span className="sd-actionico">📥</span> View Orders
                  </button>
                </div>

                <div className="sd-sidecard">
                  <div className="sd-sidecard-row">
                    <div className="sd-sidecard-title">Reports</div>
                    <button className="sd-dd" onClick={goReports || (() => {})}>
                      This Week ▾
                    </button>
                  </div>

                  <div className="sd-report">
                    <div className="sd-report-y">
                      <div>GHS 3K</div>
                      <div>GHS 2K</div>
                      <div>GHS 1K</div>
                    </div>

                    <div className="sd-report-chart">
                      <svg viewBox="0 0 260 110" preserveAspectRatio="none">
                        <path d={reportPath} className="sd-report-line" />
                      </svg>

                      <div className="sd-report-x">
                        <span>Mo</span>
                        <span>Tu</span>
                        <span>W</span>
                        <span>Th</span>
                        <span>Fr</span>
                        <span>Sa</span>
                        <span>Su</span>
                      </div>
                    </div>
                  </div>
                </div>
              </aside>

              {/* MAIN */}
              <main className="sd-main">
                <div className="sd-welcome">
                  <div className="sd-welcome-title">
                    Welcome back, {safe(user?.displayName) || "Seller"}!
                  </div>
                  <div className="sd-welcome-sub">
                    Here&apos;s an overview of your store&apos;s activity. ✍️
                  </div>
                </div>

                {/* STATS */}
                <div className="sd-stats">
                  <div className="sd-stat sd-stat--blue">
                    <div className="sd-stat-top">
                      <div className="sd-stat-ico">🧾</div>
                      <div>
                        <div className="sd-stat-value">{newOrdersCount}</div>
                        <div className="sd-stat-label">New Orders</div>
                        <div className="sd-stat-sub">This Week</div>
                      </div>
                    </div>
                    <div className="sd-pill">
                      <span className="sd-pill-ico">🚚</span> Delivery
                    </div>
                  </div>

                  <div className="sd-stat sd-stat--green">
                    <div className="sd-stat-top">
                      <div className="sd-stat-ico">💵</div>
                      <div>
                        <div className="sd-stat-value">{moneyGHS(salesRecent).replace("GHS ", "GHS\n")}</div>
                        <div className="sd-stat-label">Total Sales</div>
                      </div>
                    </div>
                    <div className="sd-pill">
                      <span className="sd-pill-ico">🛍️</span> Local Pickup
                    </div>
                  </div>

                  <div className="sd-stat sd-stat--orange">
                    <div className="sd-stat-top">
                      <div className="sd-stat-ico">📦</div>
                      <div>
                        <div className="sd-stat-value">{lowStockCount}</div>
                        <div className="sd-stat-label">Low-stock products</div>
                      </div>
                    </div>
                    <div className="sd-pill">
                      <span className="sd-pill-ico">📍</span> Meet-up Location
                    </div>
                  </div>
                </div>

                {/* RECENT ORDERS */}
                <section className="sd-panel">
                  <div className="sd-panel-head">
                    <div className="sd-panel-title">Recent Orders</div>
                    <button className="sd-viewall" onClick={goOrders}>
                      View All <span className="sd-arrow">›</span>
                    </button>
                  </div>

                  <div className="sd-tabs">
                    <button
                      className={`sd-tab ${ordersTab === "recent" ? "is-active" : ""}`}
                      onClick={() => setOrdersTab("recent")}
                    >
                      Recent
                    </button>
                    <button
                      className={`sd-tab ${ordersTab === "all" ? "is-active" : ""}`}
                      onClick={() => setOrdersTab("all")}
                    >
                      All Orders
                    </button>
                    <button
                      className={`sd-tab ${ordersTab === "completed" ? "is-active" : ""}`}
                      onClick={() => setOrdersTab("completed")}
                    >
                      Completed
                    </button>
                  </div>

                  <div className="sd-table-wrap sd-table-wrap--orders">
                    <table className="sd-table">
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th>Payment</th>
                          <th>Total</th>
                          <th>Status / Tracking</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ordersForTable.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="sd-empty">
                              No orders yet.
                            </td>
                          </tr>
                        ) : (
                          ordersForTable.map((o) => {
                            const buyerName = safe(o?.buyer?.name) || "Customer";
                            const buyerPhone = fmtPhone(o?.buyer?.phone);
                            const payLabel = normalizePayLabel(o?.payment?.type);
                            const delLabel = normalizeDelLabel(o?.delivery?.type || "Delivery");
                            const total = moneyGHS(o?.total);
                            const status = capStatus(o?.status || "NEW");

                            const buyerMarkedPaid =
                              o?.payment?.paidByBuyer === true ||
                              safe(o?.status).toUpperCase() === "PENDING_CONFIRMATION";

                            const sellerConfirmed =
                              o?.payment?.sellerConfirmed === true ||
                              safe(o?.status).toUpperCase() === "CONFIRMED";

                            const showConfirmBtn = buyerMarkedPaid && !sellerConfirmed;

                            const step = currentStepFromOrder(o);

                            return (
                              <tr key={o.id}>
                                <td>
                                  <div className="sd-customer">
                                    <span className="sd-avatar sd-avatar--xs">
                                      <span>{initials(buyerName)}</span>
                                    </span>
                                    <div>
                                      <div className="sd-customer-name">{buyerName}</div>
                                      <div className="sd-customer-sub">{buyerPhone || "—"}</div>
                                    </div>
                                  </div>
                                </td>

                                <td>
                                  <div className="sd-paycell">
                                    <span className="sd-badge sd-badge--pay">{payLabel}</span>
                                    <span className="sd-badge sd-badge--del">{delLabel}</span>
                                  </div>
                                </td>

                                <td className="sd-money">{total}</td>

                                <td>
                                  <div className="sd-statusRow">
                                    <span className="sd-badge sd-badge--status">{status}</span>
                                    <span className="sd-badge sd-badge--ok">{stepTitle(step)}</span>

                                    {showConfirmBtn ? (
                                      <button className="sd-confirm" onClick={() => confirmPayment(o)}>
                                        ✅ Confirm
                                      </button>
                                    ) : null}

                                    <button
                                      className="sd-softAction"
                                      disabled={!sellerConfirmed}
                                      onClick={() => pushTracking(o, "PACKED", "Order packed")}
                                      title={!sellerConfirmed ? "Confirm payment first" : "Mark packed"}
                                    >
                                      📦 Packed
                                    </button>

                                    <button
                                      className="sd-softAction"
                                      disabled={!sellerConfirmed}
                                      onClick={() => pushTracking(o, "OUT_FOR_DELIVERY", "Rider picked up")}
                                      title={!sellerConfirmed ? "Confirm payment first" : "Mark out for delivery"}
                                    >
                                      🚚 Out
                                    </button>

                                    <button
                                      className="sd-softAction"
                                      disabled={!sellerConfirmed}
                                      onClick={() => pushTracking(o, "DELIVERED", "Delivered to customer")}
                                      title={!sellerConfirmed ? "Confirm payment first" : "Mark delivered"}
                                    >
                                      ✅ Delivered
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* BOTTOM GRID */}
                <div className="sd-bottom">
                  {/* Top Products */}
                  <section className="sd-panel">
                    <div className="sd-panel-head">
                      <div className="sd-panel-title">Top Products</div>
                      <button className="sd-softbtn" onClick={goManageProducts}>
                        Manage Products
                      </button>
                    </div>

                    <div className="sd-list sd-list--scroll">
                      {(products || []).map((p) => {
                        const title = safe(p.title) || "Untitled";
                        const price = moneyGHS(p.price);
                        const stock = Number(p.stock || 0);
                        const img =
                          safe(p?.thumbnailUrl) ||
                          safe(p?.imageUrl) ||
                          safe(p?.mediaUrls?.[0]) ||
                          safe(p?.imageUrls?.[0]);

                        return (
                          <div className="sd-listrow" key={p.id}>
                            <div className="sd-prod-left">
                              <div className="sd-prod-img">
                                {img ? <img src={img} alt={title} /> : <span>📦</span>}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div className="sd-prod-title">{title}</div>
                                <div className="sd-prod-sub">{stock > 0 ? `${stock} stock` : "Out of stock"}</div>
                              </div>
                            </div>

                            <div className="sd-prod-right">
                              <div className="sd-money">{price}</div>
                            </div>
                          </div>
                        );
                      })}

                      {products.length === 0 ? <div className="sd-emptybox">No products yet.</div> : null}
                    </div>

                    <div className="sd-miniStats">
                      <div className="sd-mini">
                        <div className="sd-mini-label">Total Sales</div>
                        <div className="sd-mini-val">{moneyGHS(salesRecent)}</div>
                      </div>
                      <div className="sd-mini">
                        <div className="sd-mini-label">New Orders</div>
                        <div className="sd-mini-val">{newOrdersCount}</div>
                      </div>
                    </div>

                    <div className="sd-smallnote">
                      Products (approx): <b>{productCount}</b>
                    </div>
                  </section>

                  {/* Delivery & Payments */}
                  <section className="sd-panel">
                    <div className="sd-panel-title">Delivery &amp; Payments</div>

                    <div className="sd-dphead">
                      <span className="sd-dpico">🚚</span>
                      <div>
                        <div className="sd-dptitle">Delivery Options</div>
                        <div className="sd-dpsub">
                          {enabledDeliveries.length} option{enabledDeliveries.length === 1 ? "" : "s"} available
                        </div>
                      </div>
                    </div>

                    <div className="sd-dplist">
                      {enabledPayments.slice(0, 3).map((p) => {
                        const label = safe(p.label) || normalizePayLabel(p.type) || "Payment";
                        const kind = normalizePayLabel(p.type);
                        const icon = kind === "MoMo" ? "📱" : kind === "Bank" ? "🏦" : "💵";

                        return (
                          <button
                            className="sd-dpitem"
                            key={p.id}
                            onClick={(goPaymentsAndDelivery || goSettings) || (() => {})}
                          >
                            <span className="sd-dpitem-ico">{icon}</span>
                            <span className="sd-dpitem-text">{label}</span>
                            <span className="sd-arrow">›</span>
                          </button>
                        );
                      })}

                      {enabledPayments.length === 0 ? (
                        <>
                          <button className="sd-dpitem" onClick={(goPaymentsAndDelivery || goSettings) || (() => {})}>
                            <span className="sd-dpitem-ico">📱</span>
                            <span className="sd-dpitem-text">MoMo</span>
                            <span className="sd-arrow">›</span>
                          </button>
                          <button className="sd-dpitem" onClick={(goPaymentsAndDelivery || goSettings) || (() => {})}>
                            <span className="sd-dpitem-ico">🏦</span>
                            <span className="sd-dpitem-text">Bank Transfer</span>
                            <span className="sd-arrow">›</span>
                          </button>
                          <button className="sd-dpitem" onClick={(goPaymentsAndDelivery || goSettings) || (() => {})}>
                            <span className="sd-dpitem-ico">💵</span>
                            <span className="sd-dpitem-text">Cash</span>
                            <span className="sd-arrow">›</span>
                          </button>
                        </>
                      ) : null}
                    </div>
                  </section>
                </div>
              </main>
            </div>
          </div>
        ) : null}
      </div>

      {/* ✅ QR MODAL (only opens when paid, because button is gated) */}
      {qrOpen ? (
        <div className="sd-qr-overlay" onClick={closeQr}>
          <div className="sd-qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sd-qr-title">Your Store QR Code</div>

            <div className="sd-qr-card">
              <QRCode value={publicUrl} size={200} />
            </div>

            <input className="sd-qr-input" readOnly value={publicUrl} />
            <div className="sd-qr-note">
              Scan to open your store. (On localhost, it works only on the same device/network.)
            </div>

            <div className="sd-qr-actions">
              <button
                className="sd-qr-btn"
                onClick={async () => {
                  const ok = await copyToClipboard(publicUrl);
                  setMsg(ok ? "✅ Store link copied!" : `Copy failed. Link: ${publicUrl}`);
                  setTimeout(() => setMsg(""), 1400);
                }}
              >
                Copy Link
              </button>

              <button className="sd-qr-btn" onClick={() => window.open(publicUrl, "_blank")}>
                Open
              </button>

              <button className="sd-qr-btn sd-qr-btn--primary" onClick={closeQr}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------- CSS (embedded) ---------------- */
const css = `
/* page */
.sd-page{
  min-height:100vh;
  background:#f3f6ff;
  padding:18px;
  color:#0f172a;
  font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
}

.sd-container{
  max-width:1200px;
  margin:0 auto;
  width:100%;
}

/* alerts */
.sd-alert{
  background:#ffffff;
  border:1px solid #e7ecf6;
  box-shadow:0 12px 30px rgba(15,23,42,0.06);
  padding:12px 14px;
  border-radius:14px;
  font-weight:800;
  margin-bottom:12px;
}
.sd-alert--err{
  border-color:rgba(255,70,120,0.35);
  background:rgba(255,70,120,0.06);
  color:#b91c1c;
}

/* outer card */
.sd-card--outer{
  overflow:hidden;
  border-radius:22px;
  border:1px solid #e7ecf6;
  background:#fff;
  box-shadow:0 16px 45px rgba(15,23,42,0.08);
}

/* hero */
.sd-hero{
  position:relative;
  height:210px;
  background:linear-gradient(135deg, #cfe3ff 0%, #dbeafe 40%, #e6f0ff 100%);
}
.sd-hero-img{
  position:absolute; inset:0;
  width:100%; height:100%;
  object-fit:cover;
}
.sd-hero-overlay{
  position:absolute; inset:0;
  background:linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.65));
}
.sd-hero-sign{
  position:absolute;
  left:50%;
  top:22px;
  transform:translateX(-50%);
  padding:8px;
  border-radius:14px;
  background:rgba(255,255,255,0.35);
  backdrop-filter: blur(8px);
  border:1px solid rgba(255,255,255,0.65);
}
.sd-hero-sign-inner{
  background:#2f61c5;
  color:#fff;
  font-weight:950;
  font-size:22px;
  padding:10px 22px;
  border-radius:12px;
  border:1px solid rgba(255,255,255,0.20);
  box-shadow:0 10px 24px rgba(15,23,42,0.18);
  text-align:center;
  white-space:nowrap;
  max-width:min(92vw, 680px);
  overflow:hidden;
  text-overflow:ellipsis;
}

/* topnav */
.sd-topnav{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  padding:10px 16px;
  background:#f7f9ff;
  border-bottom:1px solid #e7ecf6;
}
.sd-topnav-links{
  display:flex;
  gap:14px;
  align-items:center;
  justify-content:center;
  flex:1;
  min-width:0;
}
.sd-topnav-link{
  background:transparent;
  border:0;
  cursor:pointer;
  font-weight:900;
  color:#334155;
  padding:10px 14px;
  border-radius:999px;
}
.sd-topnav-link.is-active{
  color:#1d4ed8;
  box-shadow: inset 0 -3px 0 #1d4ed8;
  border-radius:10px;
}
.sd-topnav-right{
  display:flex;
  align-items:center;
  gap:10px;
}
.sd-topnav-actions{
  display:flex;
  align-items:center;
  gap:10px;
}
.sd-topnav-ghost{
  border:1px solid #e7ecf6;
  background:#fff;
  border-radius:12px;
  padding:8px 12px;
  font-weight:950;
  cursor:pointer;
  box-shadow:0 10px 22px rgba(15,23,42,0.05);
}

/* ✅ notifications badge */
.sd-notif-badge{
  margin-left:8px;
  display:inline-grid;
  place-items:center;
  min-width:22px;
  height:22px;
  padding:0 8px;
  border-radius:999px;
  background:#ef4444;
  color:#fff;
  font-weight:1100;
  font-size:12px;
  line-height:1;
}

/* ✅ due pill */
.sd-topnav-pill{
  border:1px solid rgba(245,158,11,0.35);
  background:rgba(245,158,11,0.12);
  border-radius:999px;
  padding:8px 10px;
  font-weight:1100;
  cursor:pointer;
}
.sd-topnav-pill.is-expired{
  border-color: rgba(255,70,120,0.35);
  background: rgba(255,70,120,0.10);
}

.sd-topnav-profile{
  display:flex;
  align-items:center;
  gap:10px;
  background:#fff;
  border:1px solid #e7ecf6;
  border-radius:12px;
  padding:8px 10px;
  cursor:pointer;
  box-shadow:0 10px 22px rgba(15,23,42,0.06);
}
.sd-topnav-name{font-weight:900;color:#0f172a;}
.sd-caret{color:#64748b;font-weight:900;}

/* hamburger */
.sd-hamb{
  display:none;
  border:1px solid #e7ecf6;
  background:#fff;
  border-radius:12px;
  padding:8px 10px;
  font-weight:1000;
  cursor:pointer;
  box-shadow:0 10px 22px rgba(15,23,42,0.05);
}

/* ✅ subscription warning banner */
.sd-subwarn{
  margin: 12px 14px 0 14px;
  border:1px solid rgba(245,158,11,0.35);
  background: rgba(245,158,11,0.10);
  border-radius:16px;
  padding:12px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
}
.sd-subwarn.is-expired{
  border-color: rgba(255,70,120,0.35);
  background: rgba(255,70,120,0.08);
}
.sd-subwarn-title{
  font-weight:1100;
  color:#0f172a;
}
.sd-subwarn-sub{
  margin-top:3px;
  font-size:12px;
  font-weight:900;
  color:#64748b;
}
.sd-subwarn-btn{
  border:0;
  background:#0f172a;
  color:#fff;
  border-radius:12px;
  padding:10px 12px;
  font-weight:1000;
  cursor:pointer;
}

/* avatars */
.sd-avatar{
  width:44px;height:44px;
  border-radius:999px;
  border:1px solid #e7ecf6;
  background:#e8f0ff;
  display:grid;
  place-items:center;
  overflow:hidden;
  font-weight:950;
  flex:0 0 auto;
}
.sd-avatar img{width:100%;height:100%;object-fit:cover;}
.sd-avatar--lg{width:58px;height:58px;border-radius:18px;}
.sd-avatar--sm{width:30px;height:30px;}
.sd-avatar--xs{width:28px;height:28px;border-radius:999px;background:#eef2ff;}

/* body layout */
.sd-body{
  display:grid;
  grid-template-columns: 320px 1fr;
  gap:14px;
  padding:14px;
  background:#f7f9ff;
  position:relative;
}

/* overlay for mobile sidebar */
.sd-overlay{
  display:none;
  position:fixed;
  inset:0;
  background:rgba(2,6,23,0.45);
  z-index:60;
}
.sd-overlay.is-open{display:block;}

/* sidebar */
.sd-sidebar{display:grid;gap:12px;min-width:0;}
.sd-sidecard{
  background:#fff;
  border:1px solid #e7ecf6;
  border-radius:18px;
  padding:12px;
  box-shadow:0 10px 26px rgba(15,23,42,0.06);
}
.sd-sidehead{
  display:flex;
  align-items:center;
  gap:12px;
  padding:8px;
  border-radius:16px;
  background:linear-gradient(180deg, rgba(59,130,246,0.12), #fff);
  border:1px solid #eef2ff;
  position:relative;
}
.sd-close{
  display:none;
  margin-left:auto;
  border:1px solid #e7ecf6;
  background:#fff;
  border-radius:12px;
  padding:8px 10px;
  font-weight:1000;
  cursor:pointer;
}
.sd-sidename{font-weight:950;}
.sd-sideactive{
  margin-top:3px;
  font-size:12px;
  font-weight:900;
  color:#64748b;
  display:flex;
  align-items:center;
  gap:8px;
}
.sd-dot{width:8px;height:8px;border-radius:999px;background:#22c55e;display:inline-block;}
.sd-sidenav{margin-top:10px;display:grid;gap:8px;}
.sd-navbtn{
  width:100%;
  text-align:left;
  padding:10px 10px;
  border-radius:12px;
  border:1px solid #eef2ff;
  background:#fff;
  cursor:pointer;
  font-weight:900;
  display:flex;
  align-items:center;
  gap:10px;
  color:#0f172a;
}
.sd-navbtn.is-active{background:#eaf2ff;border-color:#cfe3ff;}
.sd-ico{width:22px;display:inline-grid;place-items:center;}
.sd-footnote{
  margin-top:6px;
  color:#94a3b8;
  font-weight:900;
  font-size:12px;
  padding:6px 8px;
}
.sd-navtag{
  margin-left:auto;
  border:1px solid rgba(245,158,11,0.35);
  background: rgba(245,158,11,0.10);
  border-radius: 999px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 1100;
}

/* quick actions */
.sd-sidecard-title{font-weight:950;margin-bottom:10px;}
.sd-actionbtn{
  width:100%;
  text-align:left;
  padding:12px 12px;
  border-radius:14px;
  border:1px solid #eef2ff;
  background:#f5f8ff;
  cursor:pointer;
  font-weight:950;
  display:flex;
  align-items:center;
  gap:10px;
  margin-top:8px;
}
.sd-actionico{
  width:34px;height:34px;border-radius:10px;
  background:#3b82f6;
  color:#fff;
  display:grid;
  place-items:center;
  box-shadow:0 10px 22px rgba(59,130,246,0.25);
}

/* reports */
.sd-sidecard-row{display:flex;align-items:center;justify-content:space-between;gap:10px;}
.sd-dd{
  border:1px solid #e7ecf6;
  background:#fff;
  border-radius:10px;
  padding:8px 10px;
  font-weight:900;
  cursor:pointer;
  color:#334155;
}
.sd-report{
  margin-top:10px;
  display:grid;
  grid-template-columns: 70px 1fr;
  gap:10px;
  align-items:stretch;
}
.sd-report-y{
  display:grid;
  gap:16px;
  align-content:start;
  color:#94a3b8;
  font-weight:900;
  font-size:12px;
}
.sd-report-chart{
  border:1px solid #eef2ff;
  background:#f7fbff;
  border-radius:14px;
  padding:10px 10px 6px 10px;
  position:relative;
}
.sd-report-line{
  fill:none;
  stroke:#3b82f6;
  stroke-width:3.2;
  stroke-linecap:round;
  stroke-linejoin:round;
  filter: drop-shadow(0px 8px 10px rgba(59,130,246,0.18));
}
.sd-report-x{
  margin-top:6px;
  display:flex;
  justify-content:space-between;
  color:#94a3b8;
  font-weight:900;
  font-size:12px;
}

/* main */
.sd-main{display:flex;flex-direction:column;gap:12px;min-width:0;}
.sd-welcome{padding:8px 6px;}
.sd-welcome-title{font-size:22px;font-weight:1000;}
.sd-welcome-sub{margin-top:4px;color:#64748b;font-weight:900;font-size:13px;}

/* stats */
.sd-stats{display:grid;grid-template-columns: repeat(3, minmax(0,1fr));gap:12px;}
.sd-stat{border-radius:16px;border:1px solid #eef2ff;padding:12px;background:#fff;min-width:0;}
.sd-stat--blue{background:linear-gradient(180deg, rgba(59,130,246,0.10), #fff);}
.sd-stat--green{background:linear-gradient(180deg, rgba(34,197,94,0.10), #fff);}
.sd-stat--orange{background:linear-gradient(180deg, rgba(249,115,22,0.10), #fff);}
.sd-stat-top{display:flex;gap:10px;align-items:flex-start;min-width:0;}
.sd-stat-ico{
  width:34px;height:34px;border-radius:12px;
  display:grid;place-items:center;
  border:1px solid #e7ecf6;
  background:#fff;
}
.sd-stat-value{font-size:24px;font-weight:1100;line-height:1.05;white-space:pre-line;}
.sd-stat-label{margin-top:4px;font-weight:950;color:#0f172a;}
.sd-stat-sub{margin-top:2px;font-weight:900;color:#94a3b8;font-size:12px;}
.sd-pill{
  margin-top:10px;
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding:8px 10px;
  border-radius:999px;
  background:#f8fbff;
  border:1px solid #eef2ff;
  font-weight:950;
  font-size:12px;
  color:#334155;
}

/* panels */
.sd-panel{
  background:#fff;
  border:1px solid #e7ecf6;
  border-radius:18px;
  padding:12px;
  box-shadow:0 10px 26px rgba(15,23,42,0.06);
  min-width:0;
}
.sd-panel-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  margin-bottom:8px;
  min-width:0;
}
.sd-panel-title{font-weight:1000;font-size:16px;}
.sd-viewall{
  border:1px solid #e7ecf6;
  background:#f7fbff;
  border-radius:12px;
  padding:8px 12px;
  font-weight:950;
  cursor:pointer;
}
.sd-arrow{font-weight:1100;margin-left:6px;}
.sd-softbtn{
  border:1px solid #e7ecf6;
  background:#f7fbff;
  border-radius:12px;
  padding:8px 12px;
  font-weight:950;
  cursor:pointer;
}

/* tabs */
.sd-tabs{
  display:flex;
  gap:10px;
  padding:8px;
  background:#f7f9ff;
  border:1px solid #eef2ff;
  border-radius:14px;
  margin-bottom:10px;
  width:fit-content;
}
.sd-tab{
  border:0;
  background:transparent;
  padding:8px 12px;
  border-radius:12px;
  cursor:pointer;
  font-weight:950;
  color:#64748b;
}
.sd-tab.is-active{
  background:#2f61c5;
  color:#fff;
  box-shadow:0 10px 24px rgba(47,97,197,0.18);
}

/* ✅ Recent Orders: vertical scroll only */
.sd-table-wrap{
  border-radius:14px;
  border:1px solid #eef2ff;
  background:#fff;
  overflow:hidden;
}
.sd-table-wrap--orders{
  max-height: 360px;
  overflow-y:auto;
  overflow-x:hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  scrollbar-gutter: stable both-edges;
}

/* table */
.sd-table{
  width:100%;
  border-collapse:separate;
  border-spacing:0;
  table-layout: fixed;
}
.sd-table th{
  text-align:left;
  font-size:12px;
  color:#94a3b8;
  font-weight:950;
  padding:10px 12px;
  background:#fbfdff;
  border-bottom:1px solid #eef2ff;
  position: sticky;
  top: 0;
  z-index: 2;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}
.sd-table td{
  padding:12px;
  border-bottom:1px solid #f1f5ff;
  font-weight:900;
  font-size:13px;
  color:#0f172a;
  vertical-align:middle;
  min-width:0;
  overflow:hidden;
}
.sd-empty{padding:14px;color:#94a3b8;font-weight:950;}

/* customer */
.sd-customer{
  display:flex;
  align-items:flex-start;
  gap:10px;
  min-width:0;
}
.sd-customer-name{
  font-weight:1000;
  font-size:13px;
  line-height:1.2;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
  max-width:160px;
}
.sd-customer-sub{
  margin-top:2px;
  font-size:11px;
  color:#94a3b8;
  font-weight:900;
  white-space:nowrap;
}

/* payment */
.sd-paycell{
  display:flex;
  gap:8px;
  flex-wrap:wrap;
  align-items:center;
  min-width:0;
}

/* badges */
.sd-badge{
  display:inline-flex;
  align-items:center;
  padding:6px 10px;
  border-radius:999px;
  border:1px solid #e7ecf6;
  background:#f7fbff;
  font-weight:950;
  font-size:12px;
  color:#334155;
  max-width:100%;
  white-space:normal;
}
.sd-badge--del{background:#f3fff7;border-color:#dff7e6;}
.sd-badge--status{background:#fff7ed;border-color:#ffedd5;}
.sd-badge--ok{background:#f0fdf4;border-color:#bbf7d0;}
.sd-money{white-space:nowrap;}

/* status/tracking wraps */
.sd-statusRow{
  display:flex;
  gap:8px;
  align-items:center;
  flex-wrap:wrap;
  min-width:0;
}

/* buttons */
.sd-confirm{
  border:0;
  background:#0f172a;
  color:#fff;
  padding:8px 10px;
  border-radius:12px;
  font-weight:950;
  cursor:pointer;
}
.sd-softAction{
  border:1px solid #e7ecf6;
  background:#fff;
  padding:8px 10px;
  border-radius:12px;
  font-weight:950;
  cursor:pointer;
}
.sd-softAction:disabled{opacity:.55; cursor:not-allowed;}

/* bottom grid */
.sd-bottom{display:grid;grid-template-columns: 1.1fr 0.9fr;gap:12px;min-width:0;}
.sd-list{display:grid;gap:10px;margin-top:10px;}

/* top products horizontal scroll */
.sd-list--scroll{
  display:flex;
  gap:10px;
  overflow-x:auto;
  overflow-y:hidden;
  padding-right: 6px;
  padding-bottom:16px;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;
  scrollbar-gutter: stable both-edges;
}
.sd-listrow{
  flex: 0 0 auto;
  min-width: 260px;
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:12px;
  border:1px solid #eef2ff;
  background:#fbfdff;
  border-radius:14px;
  padding:10px;
}
.sd-prod-left{display:flex;align-items:center;gap:10px;min-width:0;}
.sd-prod-img{
  width:42px;height:42px;border-radius:12px;
  border:1px solid #e7ecf6;
  background:#eef2ff;
  display:grid;
  place-items:center;
  overflow:hidden;
  flex:0 0 auto;
}
.sd-prod-img img{width:100%;height:100%;object-fit:cover;}
.sd-prod-title{
  font-weight:1000;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
  max-width:260px;
}
.sd-prod-sub{font-size:12px;color:#94a3b8;font-weight:900;margin-top:2px;}
.sd-emptybox{
  border:1px dashed #dbeafe;
  border-radius:14px;
  padding:12px;
  color:#94a3b8;
  font-weight:950;
  background:#f7fbff;
}

/* mini stats */
.sd-miniStats{margin-top:12px; display:grid; grid-template-columns:1fr 1fr; gap:10px;}
.sd-mini{border:1px solid #eef2ff;border-radius:14px;padding:10px;background:#fbfdff;}
.sd-mini-label{font-size:12px;color:#94a3b8;font-weight:950;}
.sd-mini-val{margin-top:4px;font-size:18px;font-weight:1100;}
.sd-smallnote{margin-top:10px;color:#94a3b8;font-weight:950;font-size:12px;}

/* delivery & payments list */
.sd-dphead{
  display:flex;
  align-items:center;
  gap:10px;
  padding:10px;
  margin-top:10px;
  border:1px solid #eef2ff;
  border-radius:14px;
  background:#fbfdff;
}
.sd-dpico{
  width:36px;height:36px;border-radius:12px;
  border:1px solid #e7ecf6;
  background:#fff;
  display:grid;
  place-items:center;
}
.sd-dptitle{font-weight:1000;}
.sd-dpsub{font-size:12px;color:#94a3b8;font-weight:950;margin-top:2px;}
.sd-dplist{margin-top:10px;display:grid;gap:10px;}
.sd-dpitem{
  width:100%;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  padding:10px 12px;
  border-radius:14px;
  border:1px solid #eef2ff;
  background:#fbfdff;
  cursor:pointer;
  font-weight:1000;
}
.sd-dpitem-ico{
  width:36px;height:36px;border-radius:12px;
  display:grid;
  place-items:center;
  background:#eef2ff;
  border:1px solid #e7ecf6;
}
.sd-dpitem-text{flex:1;text-align:left;min-width:0;}

/* ✅ QR modal */
.sd-qr-overlay{
  position:fixed;
  inset:0;
  background:rgba(2,6,23,0.55);
  display:grid;
  place-items:center;
  padding:16px;
  z-index:9999;
}
.sd-qr-modal{
  width:min(440px, 100%);
  background:#fff;
  border:1px solid #e7ecf6;
  border-radius:18px;
  box-shadow:0 20px 60px rgba(0,0,0,.22);
  padding:14px;
  display:grid;
  gap:10px;
}
.sd-qr-title{font-weight:1100;font-size:16px;}
.sd-qr-card{
  width:max-content;
  background:#fff;
  border:1px solid #e7ecf6;
  border-radius:14px;
  padding:12px;
}
.sd-qr-input{
  width:100%;
  border:1px solid #cbd5e1;
  border-radius:12px;
  padding:10px;
  font-weight:900;
  outline:none;
  background:#fff;
}
.sd-qr-note{
  color:#64748b;
  font-weight:900;
  font-size:12px;
}
.sd-qr-actions{
  display:flex;
  gap:10px;
  justify-content:flex-end;
  flex-wrap:wrap;
}
.sd-qr-btn{
  border:1px solid #e7ecf6;
  background:#f7fbff;
  border-radius:12px;
  padding:8px 12px;
  font-weight:950;
  cursor:pointer;
}
.sd-qr-btn--primary{
  border:0;
  background:#2f61c5;
  color:#fff;
  box-shadow:0 10px 22px rgba(47,97,197,0.18);
}

/* responsive */
@media (max-width: 1100px){
  .sd-topnav{
    flex-wrap: wrap;
    gap: 10px;
  }
  .sd-topnav-links{
    justify-content:flex-start;
    flex: 1 1 260px;
    min-width: 220px;
  }
  .sd-topnav-actions{
    display:flex;
    flex: 1 1 100%;
    justify-content:flex-start;
    flex-wrap:wrap;
    gap:10px;
    order: 3;
  }
  .sd-topnav-right{
    flex-wrap:wrap;
    justify-content:flex-end;
  }
  .sd-topnav-ghost{
    padding:8px 10px;
  }
}

@media (max-width: 980px){
  .sd-hamb{display:inline-flex;}
  .sd-body{grid-template-columns:1fr;}
  .sd-stats{grid-template-columns:1fr;}
  .sd-bottom{grid-template-columns:1fr;}
  .sd-hero-sign-inner{font-size:18px;}

  .sd-sidebar{
    position:fixed;
    top:0;
    left:0;
    height:100vh;
    width:min(92vw, 360px);
    background:#f7f9ff;
    padding:14px;
    overflow:auto;
    transform:translateX(-110%);
    transition:transform 0.18s ease;
    z-index:70;
  }
  .sd-sidebar.is-open{transform:translateX(0%);}
  .sd-close{display:inline-flex;}
  .sd-table-wrap--orders{max-height: 340px;}
}

/* mobile tight spacing */
@media (max-width: 520px){
  .sd-page{padding:10px;}
  .sd-container{max-width:100%;}
  .sd-card--outer{border-radius:16px;}
  .sd-body{padding:10px; gap:10px;}
  .sd-main{gap:10px;}
  .sd-bottom{gap:10px;}
  .sd-panel{padding:10px; border-radius:16px;}
  .sd-panel-head{margin-bottom:6px;}
  .sd-topnav{padding:10px 12px;}
  .sd-hero{height:180px;}
  .sd-hero-sign{top:14px;}
  .sd-hero-sign-inner{padding:9px 12px; font-size:17px;}
  .sd-tabs{width:100%;justify-content:space-between;margin-bottom:8px;}
  .sd-tab{flex:1;text-align:center;padding:8px 8px;}

  .sd-topnav-actions{
    width:100%;
    gap:8px;
  }
  .sd-topnav-ghost{
    border-radius:12px;
    padding:8px 10px;
    font-size:12px;
  }

  .sd-table-wrap--orders{max-height: 300px;}
  .sd-table td{padding:10px;}
  .sd-table th{padding:9px 10px; font-size:11px;}

  .sd-badge{padding:5px 8px; font-size:11px;}
  .sd-statusRow{gap:6px;}

  .sd-customer-name{font-size:12.5px; max-width:140px;}
  .sd-customer-sub{font-size:10.5px;}

  .sd-list{margin-top:8px; gap:8px;}
  .sd-list--scroll{padding-bottom:12px; gap:8px;}
  .sd-listrow{min-width:220px; padding:9px; border-radius:14px;}
  .sd-prod-title{max-width:200px;}

  .sd-miniStats{margin-top:10px; gap:8px;}
  .sd-mini{padding:9px;}
}
`;
